import { GridSortModel } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useDispatch } from 'react-redux';

import { EMPTY, Observable, Subscription, tap } from 'rxjs';

import { MatchType } from 'app/constants/match-type';
import { TimingConstants } from 'app/constants/timing-constants';
import { LocationMatchType, MccMatchEnum } from 'app/enum';
import { ListOptions, MidTuple } from 'app/model';
import { AsyncSearchResponse } from 'app/model/es/async-search.response';
import { MidFilter } from 'app/model/mid-filter';
import { mlSearchService } from 'app/services/machine-learning/ml-search.service';
import { WithEsId } from 'app/type/with-es-id';
import { startLoading, stopLoading } from 'features/top-loader/loader.slice';

interface UseMLTupleSearchReturn {
    tuples: Array<Array<WithEsId<MidTuple>>>;
    findTuples: (filters: MidFilter[], sort?: GridSortModel, sampleId?: number) => void;
    loadMore: () => Observable<AsyncSearchResponse<MidTuple>>;
    isEstimate: boolean;
    total: number;
    totalLabel: string;
}

const parseSort = (gridSort?: GridSortModel): ListOptions => {
    const model = gridSort?.[0];

    if (model && model.sort) {
        return {
            sort: { [model.field]: model.sort },
        };
    }

    return {};
};

export const useMLTupleSearch = (
    cobrander: string,
    correction: boolean,
    storeField: boolean,
    storeId?: number,
    isNegative?: boolean,
    isTagStore?: boolean,
): UseMLTupleSearchReturn => {
    const dispatch = useDispatch();
    const [tuples, setTuples] = useState<Array<Array<WithEsId<MidTuple>>>>([]);
    const [isEstimate, setIsEstimate] = useState(false);
    const [total, setTotal] = useState(0);

    const index = useRef<number>();
    const lastSearch = useRef<MidFilter[]>();
    const lastSort = useRef<GridSortModel>();
    const lastSampleId = useRef<number | undefined>();
    const searchAfter = useRef<Array<number | string>>();

    const activeTimer = useRef<ReturnType<typeof setTimeout>>();
    const activeSub = useRef<Subscription>();

    const cleanup = useCallback(() => {
        if (activeTimer.current !== undefined) {
            clearTimeout(activeTimer.current);
            dispatch(stopLoading());
        }
        if (activeSub.current) {
            activeSub.current.unsubscribe();
        }
    }, [dispatch]);

    useEffect(() => {
        return cleanup;
    }, [cleanup]);

    const scheduleAsyncCompletionFetch = useCallback(
        (loadIndex: number, response: AsyncSearchResponse<MidTuple>) => {
            if (!response.id || !response.isRunning) {
                dispatch(stopLoading());

                return;
            }

            activeTimer.current = setTimeout(() => {
                activeSub.current = mlSearchService.asyncResults<MidTuple>(response.id).subscribe((results) => {
                    setTuples((current) => {
                        current[loadIndex] = results.toHitSources();

                        return [...current];
                    });
                    setIsEstimate('gte' === results.response.total.relation);
                    setTotal(results.response.total.value ?? 0);
                    searchAfter.current = results.lastSort();
                    scheduleAsyncCompletionFetch(loadIndex, results);
                });
            }, TimingConstants.ASYNC_RESULTS_INTERVAL);
        },
        [dispatch],
    );

    const doSearch = useCallback(
        (sampleId?: number): Observable<AsyncSearchResponse<MidTuple>> => {
            if (lastSearch.current === undefined) {
                throw new Error('Tried to search but last search is not set');
            }

            if (index.current === undefined) {
                throw new Error('Tried to search but index is not set');
            }

            const idx = index.current;

            return mlSearchService
                .findTuples(
                    {
                        cobrander,
                        matchOperator: MatchType.REGEX,
                        locationOperator: LocationMatchType.REGEX,
                        mccMatchOperator: MccMatchEnum.IN,
                        mccPattern: [],
                    },
                    lastSearch.current,
                    correction ? '1' : '0',
                    storeField ? true : false,
                    storeId,
                    searchAfter.current,
                    parseSort(lastSort.current),
                    sampleId,
                    isNegative,
                    isTagStore,
                )
                .pipe(
                    tap((results) => {
                        dispatch(startLoading());
                        setIsEstimate('gte' === results.response.total.relation);
                        setTotal(results.response.total.value ?? 0);
                        scheduleAsyncCompletionFetch(idx, results);
                    }),
                );
        },
        [cobrander, correction, storeField, dispatch, scheduleAsyncCompletionFetch, storeId, isNegative, isTagStore],
    );

    const findTuples = useCallback(
        (filters: MidFilter[], sort?: GridSortModel, sampleId?: number) => {
            searchAfter.current = undefined;
            lastSearch.current = filters;
            lastSort.current = sort;
            lastSampleId.current = sampleId;
            index.current = 0;

            cleanup();

            doSearch(sampleId).subscribe((results) => {
                setTuples([results.toHitSources()]);
                searchAfter.current = results.lastSort();
            });
        },
        [cleanup, doSearch],
    );

    const loadMore = useCallback((): Observable<AsyncSearchResponse<MidTuple>> => {
        if (!searchAfter.current || index.current === undefined) {
            return EMPTY;
        }
        index.current++;

        return doSearch(lastSampleId.current);
    }, [doSearch]);

    const totalLabel = `${total.toLocaleString()}${isEstimate ? '+' : ''}`;

    return { tuples, findTuples, loadMore, isEstimate, total, totalLabel };
};
